import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { LandingLogo, EASE } from "./shared";

const LINKS = [
  { label: "Home", href: "#home" },
  { label: "Platform", href: "#platform" },
  { label: "Solutions", href: "#solutions" },
  { label: "Resources", href: "#resources" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: EASE }}
      data-testid="landing-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-white/75 backdrop-blur-2xl border-b border-slate-200/70 shadow-[0_8px_30px_rgba(15,23,42,0.06)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 h-[72px] flex items-center justify-between gap-6">
        <LandingLogo />

        <div className="hidden lg:flex items-center gap-1">
          {LINKS.map((link) => (
            <a
              key={link.label}
              href={link.href}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className="px-3.5 py-2 text-sm font-medium text-slate-600 rounded-full transition-colors duration-200 hover:text-[#2563EB] hover:bg-[#2563EB]/5"
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/login"
            data-testid="nav-login-button"
            className="px-5 py-2.5 text-sm font-semibold text-[#0F172A] rounded-full border border-slate-200 transition-[border-color,color,box-shadow] duration-300 hover:border-[#2563EB]/40 hover:text-[#2563EB] hover:shadow-[0_8px_24px_rgba(15,23,42,0.08)]"
          >
            Login
          </Link>
          <a
            href="#contact"
            data-testid="nav-book-demo-button"
            className="group inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-full bg-[#2563EB] shadow-[0_10px_26px_rgba(37,99,235,0.35)] transition-[background-color,box-shadow,transform] duration-300 hover:bg-[#1D4ED8] hover:-translate-y-0.5 active:scale-[0.98]"
          >
            Book Demo
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={2} />
          </a>
        </div>

        <button
          type="button"
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-[#0F172A]"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" strokeWidth={1.8} /> : <Menu className="w-5 h-5" strokeWidth={1.8} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-2xl border-b border-slate-200"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {LINKS.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                  className="px-3 py-3 text-sm font-medium text-slate-700 rounded-xl hover:bg-[#2563EB]/5 hover:text-[#2563EB] transition-colors duration-200"
                >
                  {link.label}
                </a>
              ))}
              <div className="flex gap-3 pt-3 pb-1">
                <Link
                  to="/login"
                  data-testid="nav-mobile-login-button"
                  className="flex-1 text-center px-5 py-3 text-sm font-semibold text-[#0F172A] rounded-full border border-slate-200"
                >
                  Login
                </Link>
                <a
                  href="#contact"
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-book-demo-button"
                  className="flex-1 text-center px-5 py-3 text-sm font-semibold text-white rounded-full bg-[#2563EB]"
                >
                  Book Demo
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
